import {
  ActionIcon,
  Box,
  Burger,
  Button,
  Divider,
  Drawer,
  Group,
  Menu,
  Stack,
  Text,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconBolt, IconChevronDown, IconMoon, IconLogout, IconPhoto, IconSun, IconUser, IconWand } from "@tabler/icons-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppSelector } from "../../store/hooks";
import { GENERATORS, galleryPath, generatePath } from "../../config/generators";

/** Brand mark + wordmark, links home. */
function Logo() {
  return (
    <Box component={Link} to="/" style={{ textDecoration: "none" }}>
      <Group gap={8} wrap="nowrap">
        <Box
          w={28}
          h={28}
          style={{
            borderRadius: 8,
            background: "linear-gradient(135deg, var(--mantine-color-brand-5), var(--mantine-color-brand-7))",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <IconBolt size={16} color="#fff" />
        </Box>
        <Text fw={700} size="lg" c="var(--mantine-color-text)">
          JustScript
        </Text>
      </Group>
    </Box>
  );
}

export default function AppNavbar() {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  const navVariant = (path: string) =>
    pathname === path || (path.length > 1 && pathname.startsWith(path + "/")) ? "light" : "subtle";

  const generateIsActive = pathname.startsWith("/generate") || pathname === "/serverless";
  const accountIsActive = pathname === "/account" || pathname === "/logout";

  const themeToggle = (
    <ActionIcon variant="subtle" size="lg" onClick={toggleColorScheme} aria-label="Toggle theme">
      {colorScheme === "dark" ? <IconMoon size={18} /> : <IconSun size={18} />}
    </ActionIcon>
  );

  const sectionMenu = (
    label: string,
    icon: ReactNode,
    active: boolean,
    items: { label: string; icon: ReactNode; to: string }[],
    position: "bottom-start" | "bottom-end" = "bottom-start",
  ) => (
    <Menu position={position} width={180} withinPortal>
      <Menu.Target>
        <Button
          variant={active ? "light" : "subtle"}
          leftSection={icon}
          rightSection={<IconChevronDown size={14} />}
          size="sm"
        >
          {label}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {items.map((item) => (
          <Menu.Item key={item.to} leftSection={item.icon} onClick={() => navigate(item.to)}>
            {item.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );

  const mobileSection = (label: string, items: { label: string; icon: ReactNode; to: string }[]) => (
    <Stack gap={4}>
      <Text size="xs" fw={600} c="dimmed" px="xs">
        {label}
      </Text>
      {items.map((item) => (
        <Button
          key={item.to}
          component={Link}
          to={item.to}
          variant={navVariant(item.to)}
          leftSection={item.icon}
          size="sm"
          justify="flex-start"
          onClick={closeDrawer}
        >
          {item.label}
        </Button>
      ))}
    </Stack>
  );

  const galleryItems = GENERATORS.map((g) => ({ label: g.label, icon: <g.icon size={16} />, to: galleryPath(g) }));
  const generateItems = GENERATORS.map((g) => ({ label: g.label, icon: <g.icon size={16} />, to: generatePath(g) }));
  const accountItems = [
    { label: "Account", icon: <IconUser size={16} />, to: "/account" },
    { label: "Logout", icon: <IconLogout size={16} />, to: "/logout" },
  ];

  return (
    <>
      <Group h="100%" px="md" justify="space-between">
        <Logo />

        <Group gap="xs" visibleFrom="sm">
          {isAuthenticated && sectionMenu("Gallery", <IconPhoto size={16} />, navVariant("/gallery") === "light", galleryItems)}
          {isAuthenticated && sectionMenu("Generate", <IconWand size={16} />, generateIsActive, generateItems)}
          {isAuthenticated && sectionMenu("Account", <IconUser size={16} />, accountIsActive, accountItems, "bottom-end")}
          {!isAuthenticated && (
            <Button
              component={Link}
              to="/account"
              variant={navVariant("/account")}
              leftSection={<IconUser size={16} />}
              size="sm"
            >
              Account
            </Button>
          )}
          {themeToggle}
        </Group>

        <Group gap="xs" hiddenFrom="sm">
          {themeToggle}
          <Burger opened={drawerOpened} onClick={openDrawer} size="sm" aria-label="Open menu" />
        </Group>
      </Group>

      <Drawer opened={drawerOpened} onClose={closeDrawer} title="Menu" hiddenFrom="sm" size="xs">
        <Stack gap="xs">
          {isAuthenticated && mobileSection("Gallery", galleryItems)}
          {isAuthenticated && <Divider my={4} />}
          {isAuthenticated && mobileSection("Generate", generateItems)}
          {isAuthenticated && <Divider my={4} />}
          {isAuthenticated ? (
            mobileSection("Account", accountItems)
          ) : (
            <Button
              component={Link}
              to="/account"
              variant={navVariant("/account")}
              leftSection={<IconUser size={16} />}
              size="sm"
              justify="flex-start"
              onClick={closeDrawer}
            >
              Account
            </Button>
          )}
        </Stack>
      </Drawer>
    </>
  );
}
